import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Modal,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid,
    useTheme, alpha, TextField, InputAdornment, Tooltip, Chip, Card, CardContent,
    Snackbar, Zoom, TablePagination, TableSortLabel, Switch, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConstructionIcon from '@mui/icons-material/Construction';
import BuildIcon from '@mui/icons-material/Build';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CreateAssetForm from './CreateAssetForm'; // Path corrected after moving AssetsPage
import {
  assetsApi,
  organizationsApi,
  networksApi,
  sectionsApi,
  classificationApi,
  ratingApi
} from '../../services/api';

// Import authentication context
import { useAuth } from '../../context/AuthContext';
import EditAssetRatingDialog from './EditAssetRatingDialog';

// Import our new filter components
import AssetFilters from './controls/AssetFilters';
import FilterChips from './controls/FilterChips';

// Style for the Create modal
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 800,
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 0, // Remove padding as CreateAssetForm has its own padding
};

// Status configuration for styling and icons - matching asset_status_enum values and card colors
const statusConfig = {
  'under_construction': { 
    color: '#ff7043', 
    backgroundColor: alpha('#ff7043', 0.1), 
    icon: <ConstructionIcon fontSize="small" /> 
  },
  'under_renovation': { 
    color: '#9c27b0', 
    backgroundColor: alpha('#9c27b0', 0.1), 
    icon: <BuildIcon fontSize="small" /> 
  },
  'active': { 
    color: '#4caf50', 
    backgroundColor: alpha('#4caf50', 0.1), 
    icon: <CheckCircleIcon fontSize="small" /> 
  },
  'maintenance': { 
    color: '#2196f3', 
    backgroundColor: alpha('#2196f3', 0.1), 
    icon: <EngineeringIcon fontSize="small" /> 
  },
  'inactive': { 
    color: '#607d8b', 
    backgroundColor: alpha('#607d8b', 0.1), 
    icon: <PauseCircleIcon fontSize="small" /> 
  },
  'decommissioned': { 
    color: '#f44336', 
    backgroundColor: alpha('#f44336', 0.1), 
    icon: <CancelIcon fontSize="small" /> 
  }
};

const AssetsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Authentication context
  const { user, isLoading: authLoading, hasDiscipline } = useAuth();
  
  // State for Create Modal
  const [openCreateModal, setOpenCreateModal] = useState(false);

  // State for Asset List
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Sorting state
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  // State for Detail Modal
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedAssetData, setSelectedAssetData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [isRatingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedAssetForRating, setSelectedAssetForRating] = useState(null);
  const [criticalToggleLoading, setCriticalToggleLoading] = useState(false);

  // Check if user can set critical flag (Admin/Manager only)
  const canSetCritical = user && ['admin', 'manager'].includes(user.role);

  // State for Snackbar notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Filter drawer state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    organization_id: '',
    network_id: '',
    section_id: '',
    region: '',
    category_id: '',
    type_id: '',
    subtype_id: '',
    status: [],
    installDateFrom: null,
    installDateTo: null,
    rating_level_id: '', // Add rating filter
    is_critical: '', // Critical asset filter
  });
  
  // Lookup data for filter dropdowns
  const [filterLookupData, setFilterLookupData] = useState({
    organizations: [],
    networks: [],
    sections: [],
    categories: [],
    types: [],
    subtypes: [],
    ratingLevels: [], // Add rating levels
    ratingSchemes: [],
  });

  // Define fetchAssets using useCallback outside useEffect
  const fetchAssetsAndLookups = useCallback(async () => {
    if (authLoading || !user) {
      // Don't fetch data if authentication is still loading or user is not available
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Determine query parameters based on user role and organization
      let queryParams = { limit: 1000 };
      
      // System Admin can see all organizations, others are scoped to their organization
      if (user.role !== 'system_admin' && user.organizationId) {
        queryParams.organization_id = user.organizationId;
      }
      
      // For users with network/section-level permissions, add granular filtering
      if (user.permissionScope === 'network_section') {
        // Build combined filter for networks and sections
        const accessibleNetworkIds = user.accessible_networks || [];
        const accessibleSectionIds = user.accessible_sections || [];
        
        // If user has both network and section access, we need to show:
        // 1. All assets in accessible networks (regardless of section)
        // 2. Assets in accessible sections (even if from non-accessible networks)
        if (accessibleNetworkIds.length > 0) {
          queryParams.network_ids = accessibleNetworkIds.join(',');
        }
        if (accessibleSectionIds.length > 0) {
          queryParams.section_ids = accessibleSectionIds.join(',');
        }
        
        console.log(`🔒 [AssetsPage] Applying permission-based filtering:`, {
          networkIds: queryParams.network_ids,
          sectionIds: queryParams.section_ids
        });
      }
      
      console.log(`[AssetsPage] Fetching assets with params:`, queryParams);
      
      // Check if network/section user has any permissions at all
      if (user.permissionScope === 'network_section') {
        const hasNetworkAccess = user.accessible_networks?.length > 0;
        const hasSectionAccess = user.accessible_sections?.length > 0;
        
        if (!hasNetworkAccess && !hasSectionAccess) {
          console.warn(`[AssetsPage] User ${user.username} has no network or section permissions`);
          setAssets([]);
          setError('You do not have access to any networks or sections. Please contact your administrator to assign permissions.');
          return;
        }
      }
      
      // Fetch Assets with organization/permission scoping
      const assetsResponse = await assetsApi.getAll(queryParams);
      setAssets(assetsResponse.data || []); // Ensure assets is always an array

      // Prepare lookup data query parameters
      let lookupParams = {};
      if (user.role !== 'system_admin' && user.organizationId) {
        lookupParams.organization_id = user.organizationId;
      }

      // Fetch All Lookup Data Concurrently with organization scoping
      const lookupResults = await Promise.allSettled([
        // Organizations: System Admin sees all, others see only their org
        user.role === 'system_admin' ? 
          organizationsApi.getAll() : 
          organizationsApi.getById(user.organizationId),
        networksApi.getAll(lookupParams),
        sectionsApi.getAll(lookupParams),
        classificationApi.getCategories(),
        classificationApi.getTypes(),
        classificationApi.getSubtypes(),
        ratingApi.getSchemes(lookupParams),
      ]);

      const [
        orgResult,
        netResult,
        secResult,
        catResult,
        typeResult,
        subtypeResult,
        ratingSchemesResult,
      ] = lookupResults;

      // Helper to extract data or return empty array on failure
      const getDataOrDefault = (result, name) => {
        if (result.status === 'fulfilled') {
          const responseData = result.value?.data;
          // Handle both direct arrays (like ratingSchemes) and object-wrapped arrays (like assetsApi)
          if (Array.isArray(responseData)) {
            return responseData;
          }
          if (Array.isArray(responseData?.data)) {
            return responseData.data;
          }
          if (responseData) { // Handles single object responses like getById
            return [responseData];
          }
        }
        if (result.status === 'rejected') {
          console.error(`Error fetching ${name}:`, result.reason);
        }
        return []; // Return empty array on failure or if data is missing
      };

      // Get lookup data and apply user permission scoping
      let organizations = getDataOrDefault(orgResult, 'organizations');
      let networks = getDataOrDefault(netResult, 'networks');
      let sections = getDataOrDefault(secResult, 'sections');
      const categories = getDataOrDefault(catResult, 'categories');
      const types = getDataOrDefault(typeResult, 'types');
      const subtypes = getDataOrDefault(subtypeResult, 'subtypes');
      const ratingSchemes = getDataOrDefault(ratingSchemesResult, 'ratingSchemes');

      // Apply permission-based filtering for network/section-level users
      if (user.permissionScope === 'network_section') {
        const accessibleNetworkIds = user.accessible_networks || [];
        const accessibleSectionIds = user.accessible_sections || [];

        let authorizedNetworks = new Map();
        let authorizedSections = new Map();

        // The API pre-filters the lookup data. We need to reconstruct the context.
        
        // 1. Add directly accessible sections
        const userSections = sections.filter(s => accessibleSectionIds.includes(s.id));
        userSections.forEach(s => authorizedSections.set(s.id, s));

        // 2. Reconstruct parent networks from the accessible sections
        userSections.forEach(s => {
          if (!authorizedNetworks.has(s.network_id)) {
            // Manually create a network object if it's not in the pre-filtered list
            authorizedNetworks.set(s.network_id, {
              id: s.network_id,
              name: s.network_name || `Network ${s.network_id}`, // Fallback name
              organization_id: user.organizationId,
            });
          }
        });

        // 3. Add directly accessible networks and their child sections
        const userNetworks = networks.filter(n => accessibleNetworkIds.includes(n.id));
        userNetworks.forEach(n => authorizedNetworks.set(n.id, n));
        const childSections = sections.filter(s => accessibleNetworkIds.includes(s.network_id));
        childSections.forEach(s => authorizedSections.set(s.id, s));
        
        networks = Array.from(authorizedNetworks.values());
        sections = Array.from(authorizedSections.values());

        console.log(`🔒 [AssetsPage] Reconstructed permission context for ${user.username}:`, {
          finalAvailableNetworks: networks.map(n => n.id),
          finalAvailableSections: sections.map(s => s.id),
        });
      }

      // Filter lookup data based on user's disciplines
      const authorizedCategories = categories.filter(category => hasDiscipline(category.name));
      const authorizedCategoryIds = authorizedCategories.map(c => c.id);
      
      const authorizedTypes = types.filter(type => authorizedCategoryIds.includes(type.category_id));
      const authorizedTypeIds = authorizedTypes.map(t => t.id);

      const authorizedSubtypes = subtypes.filter(subtype => authorizedTypeIds.includes(subtype.type_id));

      // Set the complete lookup data state with permission filtering applied
      setFilterLookupData({
        organizations,
        networks,
        sections,
        categories: authorizedCategories,
        types: authorizedTypes,
        subtypes: authorizedSubtypes,
        ratingSchemes,
      });

      console.log('[AssetsPage] Rating Schemes in lookup data:', ratingSchemes);
      console.log(`[AssetsPage] Successfully loaded ${assetsResponse.data?.length || 0} assets for user ${user.email} (${user.role})`);

    } catch (err) {
      console.error("Error fetching assets or lookups:", err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to fetch initial data.';
      if (err.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view these assets.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setAssets([]); // Reset assets on error
      setFilterLookupData({ // Reset lookups on error
        organizations: [], networks: [], sections: [],
        categories: [], types: [], subtypes: [], ratingSchemes: [],
      });
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]); // Dependencies: user object and auth loading state

  // Fetch assets when component mounts
  useEffect(() => {
    fetchAssetsAndLookups(); // Call the combined fetch function
  }, [fetchAssetsAndLookups]); // Dependency is the memoized fetch function

  // Fetch asset details when an ID is selected
  useEffect(() => {
    if (!selectedAssetId) return;
    const fetchAssetDetails = async () => {
      try {
        setDetailLoading(true);
        setDetailError(null);
        setSelectedAssetData(null);
        const response = await assetsApi.getById(selectedAssetId);
        setSelectedAssetData(response.data);
      } catch (err) {
        console.error(`Error fetching asset details for ID ${selectedAssetId}:`, err);
        setDetailError(err.message || 'Failed to fetch asset details.');
      } finally {
        setDetailLoading(false);
      }
    };
    fetchAssetDetails();
  }, [selectedAssetId]);

  // --- Handlers ---
  const handleOpenCreateModal = () => setOpenCreateModal(true);
  const handleCloseCreateModal = () => setOpenCreateModal(false);

  const handleAssetClick = useCallback((id) => {
    setSelectedAssetId(id);
    setOpenDetailModal(true);
  }, []);

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedAssetId(null);
    setSelectedAssetData(null);
    setDetailError(null);
  };

  const handleViewOnMap = (asset, event) => {
    event.stopPropagation(); // Prevent the row's onClick from firing
    navigate('/map-view', { state: { selectedAsset: asset, source: 'assetsPage' } });
  };

  // Handle successful form submission
  const handleFormSubmit = useCallback(async (formData, errorInfo) => {
    // Check if there was an error during asset creation
    if (errorInfo && errorInfo.error) {
      setSnackbarMessage(`Failed to create asset: ${errorInfo.error}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return; // Don't close modal so user can fix the error
    }
    
    // API call is handled within CreateAssetForm.
    // Re-fetch assets to update the list *before* closing the modal
    try {
      await fetchAssetsAndLookups(); // Re-fetch assets AND lookups
      // Show success notification
      setSnackbarMessage(`Asset "${formData.name}" was successfully created!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseCreateModal(); // Close modal only after fetching is done
    } catch (err) {
      console.error("Error re-fetching assets after creation:", err);
      // Show error notification
      setSnackbarMessage(`Asset created, but failed to refresh assets list: ${err.message || 'Unknown error'}`);
      setSnackbarSeverity('warning'); // Use warning instead of error since the creation was successful
      setSnackbarOpen(true);
      handleCloseCreateModal();
    }
  }, [fetchAssetsAndLookups, handleCloseCreateModal]); // Update dependency

  // Handle closing the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle opening/closing the filter drawer
  const handleOpenFilterDrawer = () => setFilterDrawerOpen(true);
  const handleCloseFilterDrawer = () => setFilterDrawerOpen(false);

  // Handle applying filters
  const handleApplyFilters = (newFilters) => {
    // Make sure we have the category name for display in filter chips
    if (newFilters.category_id) {
      // Find the category in the existing categories lookup data
      const selectedCategory = filterLookupData.categories.find(cat => cat.id === newFilters.category_id);
      
      // If this category isn't in our lookup data yet, add it
      if (!selectedCategory) {
        // Try to find a matching asset to extract category name
        const assetWithCategory = assets.find(asset => 
          asset.category_id === newFilters.category_id || 
          asset.category_name === newFilters.category_id
        );
        
        if (assetWithCategory) {
          // Add this category to our lookup data
          setFilterLookupData(prev => ({
            ...prev,
            categories: [
              ...prev.categories,
              { 
                id: newFilters.category_id, 
                name: assetWithCategory.category_name || `Category ${newFilters.category_id}`
              }
            ]
          }));
        }
      }
    }
    
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };
  
  // Handle removing a single filter
  const handleRemoveFilter = (filterName, filterValue) => {
    if (filterName === 'status' && filterValue) {
      // For status array, remove only the specific value
      setFilters({
        ...filters,
        status: filters.status.filter(value => value !== filterValue)
      });
    } else {
      // For other filters, reset to default
      const resetValue = filterName.includes('Date') ? null : '';
      setFilters({
        ...filters,
        [filterName]: resetValue
      });
      
      // Reset dependent fields
      if (filterName === 'organization_id') {
        setFilters(prev => ({
          ...prev,
          organization_id: '',
          network_id: '',
          section_id: '',
        }));
      } else if (filterName === 'network_id') {
        setFilters(prev => ({
          ...prev,
          network_id: '',
          section_id: '',
        }));
      } else if (filterName === 'category_id') {
        setFilters(prev => ({
          ...prev,
          category_id: '',
          type_id: '',
          subtype_id: '',
          rating_level_id: '', // Reset rating when category changes
        }));
      } else if (filterName === 'type_id') {
        setFilters(prev => ({
          ...prev,
          type_id: '',
          subtype_id: '',
          rating_level_id: '', // Reset rating when type changes
        }));
      }
    }
    
    setPage(0); // Reset to first page when filters change
  };
  
  // Handle clearing all filters
  const handleClearFilters = () => {
    setFilters({
      organization_id: '',
      network_id: '',
      section_id: '',
      region: '',
      category_id: '',
      type_id: '',
      subtype_id: '',
      status: [],
      installDateFrom: null,
      installDateTo: null,
      rating_level_id: '', // Add rating filter to clear
    });
    setPage(0); // Reset to first page when filters are cleared
  };

  // Handle rating data changes from AssetFilters
  const handleRatingDataChange = (ratingLevels) => {
    setFilterLookupData(prev => ({
      ...prev,
      ratingLevels: ratingLevels
    }));
  };

  // Handle sorting
  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleEditRating = (asset, event) => {
    event.stopPropagation();
    setSelectedAssetForRating(asset);
    setRatingDialogOpen(true);
  };

  const handleCloseRatingDialog = () => {
    setRatingDialogOpen(false);
    setSelectedAssetForRating(null);
  };

  const handleRatingUpdate = () => {
    fetchAssetsAndLookups(); // Re-fetch assets to show updated rating
  };

  // Handle toggling the is_critical flag
  const handleCriticalToggle = async (assetId, currentValue) => {
    if (!canSetCritical) return;

    setCriticalToggleLoading(true);
    try {
      console.log(`[AssetsPage] Toggling is_critical for asset ${assetId}: ${currentValue} -> ${!currentValue}`);
      const response = await assetsApi.update(assetId, { is_critical: !currentValue });
      console.log('[AssetsPage] Update response:', response);

      // Update the selected asset data
      setSelectedAssetData(prev => prev ? { ...prev, is_critical: !currentValue } : null);

      // Update the asset in the main list
      setAssets(prevAssets =>
        prevAssets.map(asset =>
          asset.id === assetId ? { ...asset, is_critical: !currentValue } : asset
        )
      );

      setSnackbarMessage(`Asset marked as ${!currentValue ? 'critical' : 'non-critical'}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('[AssetsPage] Failed to update critical status:', error);
      console.error('[AssetsPage] Error response:', error.response?.data);
      setSnackbarMessage(error.response?.data?.error || 'Failed to update critical status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setCriticalToggleLoading(false);
    }
  };

  // Filter assets based on all filter criteria
  const authorizedAssets = assets.filter(asset => {
    if (!asset.category_name) return true; // Always show assets without a category
    return hasDiscipline(asset.category_name);
  });

  const filteredAssets = authorizedAssets.filter(asset => {
    // Search term filtering
    const matchesSearch =
      !searchTerm || 
      String(asset.id).includes(searchTerm) ||
      asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.subtype_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Organization filtering
    if (filters.organization_id && asset.organization_id !== filters.organization_id) {
      return false;
    }
    
    // Network filtering
    if (filters.network_id && asset.network_id !== filters.network_id) {
      return false;
    }
    
    // Section filtering
    if (filters.section_id && asset.section_id !== filters.section_id) {
      return false;
    }
    
    // Region filtering
    if (filters.region && asset.region_name !== filters.region) {
      return false;
    }
    
    // Category filtering (via type)
    if (filters.category_id) {
      // Check direct category match first
      const directCategoryMatch = asset.category_id === filters.category_id;
      
      // Check category via type relationship
      const assetType = filterLookupData.types.find(t => t.id === asset.asset_type_id);
      const typeCategoryMatch = assetType && assetType.category_id === filters.category_id;
      
      // Check category via name match (for cases where IDs might not match but names do)
      const categoryName = filterLookupData.categories.find(c => c.id === filters.category_id)?.name;
      const categoryNameMatch = categoryName && asset.category_name === categoryName;
      
      // If none of the category matching methods work, filter out this asset
      if (!directCategoryMatch && !typeCategoryMatch && !categoryNameMatch) {
        return false;
      }
    }
    
    // Type filtering
    if (filters.type_id && asset.asset_type_id !== filters.type_id) {
      return false;
    }
    
    // Subtype filtering
    if (filters.subtype_id && asset.subtype_id !== filters.subtype_id) {
      return false;
    }
    
    // Status filtering
    if (filters.status && filters.status.length > 0) {
      if (!asset.status || !filters.status.includes(asset.status.toLowerCase())) {
        return false;
      }
    }
    
    // Install date filtering
    if (filters.installDateFrom && asset.install_date) {
      const installDate = new Date(asset.install_date);
      const fromDate = new Date(filters.installDateFrom);
      if (installDate < fromDate) {
        return false;
      }
    }
    
    if (filters.installDateTo && asset.install_date) {
      const installDate = new Date(asset.install_date);
      const toDate = new Date(filters.installDateTo);
      if (installDate > toDate) {
        return false;
      }
    }
    
    // Rating filtering
    if (filters.rating_level_id) {
      if (filters.rating_level_id === 'null') {
        // Filter for assets with no rating
        if (asset.rating_level_id !== null && asset.rating_level_id !== undefined) {
          return false;
        }
      } else {
        // Filter for assets with specific rating level
        if (asset.rating_level_id !== filters.rating_level_id) {
          return false;
        }
      }
    }

    // Critical asset filtering
    if (filters.is_critical !== '') {
      const isCriticalFilter = filters.is_critical === 'true' || filters.is_critical === true;
      if (asset.is_critical !== isCriticalFilter) {
        return false;
      }
    }

    return true;
  });

  // Sort the filtered assets
  const sortedAssets = [...filteredAssets].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Handle special cases for sorting
    if (sortField === 'name') {
      aValue = a.name?.toLowerCase() || '';
      bValue = b.name?.toLowerCase() || '';
    } else if (sortField === 'category_name') {
      aValue = a.category_name?.toLowerCase() || '';
      bValue = b.category_name?.toLowerCase() || '';
    } else if (sortField === 'type_name') {
      aValue = a.type_name?.toLowerCase() || '';
      bValue = b.type_name?.toLowerCase() || '';
    } else if (sortField === 'subtype_name') {
      aValue = a.subtype_name?.toLowerCase() || '';
      bValue = b.subtype_name?.toLowerCase() || '';
    } else if (sortField === 'section_name') {
      aValue = a.section_name?.toLowerCase() || '';
      bValue = b.section_name?.toLowerCase() || '';
    } else if (sortField === 'region_name') {
      aValue = a.region_name?.toLowerCase() || '';
      bValue = b.region_name?.toLowerCase() || '';
    } else if (sortField === 'status') {
      aValue = a.status?.toLowerCase() || '';
      bValue = b.status?.toLowerCase() || '';
    } else if (sortField === 'latest_inspection_date') {
      aValue = a.latest_inspection_date ? new Date(a.latest_inspection_date) : new Date(0);
      bValue = b.latest_inspection_date ? new Date(b.latest_inspection_date) : new Date(0);
    } else if (sortField === 'rating_name') {
      aValue = a.rating_name?.toLowerCase() || '';
      bValue = b.rating_name?.toLowerCase() || '';
    } else if (sortField === 'is_critical') {
      // Sort critical assets first (true before false)
      aValue = a.is_critical ? 1 : 0;
      bValue = b.is_critical ? 1 : 0;
    }

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Compare values
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Get assets for the current page
  const currentAssets = sortedAssets.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate summary statistics based on actual asset_status_enum values
  const totalAssets = filteredAssets.length;
  const underConstructionAssets = filteredAssets.filter(asset => asset.status === 'under_construction').length;
  const underRenovationAssets = filteredAssets.filter(asset => asset.status === 'under_renovation').length;
  const activeAssets = filteredAssets.filter(asset => asset.status === 'active').length;
  const maintenanceAssets = filteredAssets.filter(asset => asset.status === 'maintenance').length;
  const inactiveAssets = filteredAssets.filter(asset => asset.status === 'inactive').length;
  const decommissionedAssets = filteredAssets.filter(asset => asset.status === 'decommissioned').length;
  const criticalAssets = filteredAssets.filter(asset => asset.is_critical === true).length;

  // --- Rendering ---
  
  // Show loading while authentication is being determined
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Authenticating...
        </Typography>
      </Box>
    );
  }
  
  // Show error if user is not available (should not happen due to ProtectedRoute)
  if (!user) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">
          Authentication required. Please log in to access this page.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Assets
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Comprehensive infrastructure asset management and tracking
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <span>
            {searchTerm || Object.values(filters).some(val => 
              (Array.isArray(val) ? val.length > 0 : Boolean(val))
            )
              ? `Showing ${sortedAssets.length} of ${assets.length} assets (filtered)` 
              : `Total: ${assets.length} assets`}
          </span>
          {user?.organizationName && user.role !== 'system_admin' && (
            <>
              <span>•</span>
              <span>{user.organizationName}</span>
            </>
          )}
          {user?.role === 'system_admin' && (
            <>
              <span>•</span>
              <span style={{ fontWeight: 500, color: theme.palette.warning.main }}>
                System Admin View (All Organizations)
              </span>
            </>
          )}
          {user?.permissionScope === 'network_section' && (
            <>
              <span>•</span>
              <span style={{ fontWeight: 500, color: theme.palette.info.main }}>
                Limited Access ({(user.accessible_networks?.length || 0)} networks, {(user.accessible_sections?.length || 0)} sections)
              </span>
            </>
          )}
        </Typography>
      </Box>

      {/* Controls and Actions */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search assets..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0); // Reset to first page when searching
            }}
            sx={{
              width: { xs: '100%', sm: '240px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <Tooltip title="Filter assets">
            <IconButton 
              size="small" 
              sx={{ 
                bgcolor: 'white',
                border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                borderRadius: 2,
                padding: '8px',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
                // Highlight button when filters are active
                ...(Object.values(filters).some(val => 
                  (Array.isArray(val) ? val.length > 0 : Boolean(val))
                ) && {
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                })
              }}
              onClick={handleOpenFilterDrawer}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={
            user?.role === 'viewer' 
              ? "Viewers have read-only access and cannot create assets" 
              : "Create a new asset"
          }>
            <span>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpenCreateModal}
                disabled={user?.role === 'viewer'}
                sx={{
                  borderRadius: '8px',
                  px: 2,
                  py: 1,
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 500,
                  textTransform: 'none',
                  boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.grey[300],
                    color: theme.palette.grey[500],
                  }
                }}
              >
                {user?.role === 'viewer' ? 'View Only Mode' : 'Create New Asset'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Chips */}
      <FilterChips 
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
        onClearFilters={handleClearFilters}
        organizations={filterLookupData.organizations}
        networks={filterLookupData.networks}
        sections={filterLookupData.sections}
        categories={filterLookupData.categories}
        types={filterLookupData.types}
        subtypes={filterLookupData.subtypes}
        ratingLevels={filterLookupData.ratingLevels || []}
        assetsData={assets}
      />

      {/* Summary cards - 7 cards in a single row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(8, 1fr)'
          },
          gap: 2,
          mb: 3
        }}
      >
        {/* Total Assets */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #5b86e5, #36d1dc)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <VisibilityIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {totalAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Total Assets
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Critical Assets */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #d32f2f, #f44336)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <WarningIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {criticalAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Critical
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Under Construction */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #ff7043, #ffab40)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <ConstructionIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {underConstructionAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Under Construction
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Under Renovation */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <BuildIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {underRenovationAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Under Renovation
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Active Assets */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #4caf50, #81c784)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {activeAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Active
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Maintenance Assets */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #2196f3, #4fc3f7)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <EngineeringIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {maintenanceAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Maintenance
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Inactive Assets */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #607d8b, #90a4ae)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <PauseCircleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {inactiveAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Inactive
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Decommissioned Assets */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #f44336, #e57373)',
            color: 'white',
            minHeight: '120px'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <CancelIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {decommissionedAssets}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Decommissioned
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Asset List Table */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading all assets, please wait...
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {!loading && !error && (
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: '12px',
            boxShadow: theme.shadows[2],
            overflow: 'hidden',
            mb: 3
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="assets table">
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Asset ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 80, textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortField === 'is_critical'}
                    direction={sortField === 'is_critical' ? sortDirection : 'asc'}
                    onClick={() => handleSort('is_critical')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Critical
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'category_name'}
                    direction={sortField === 'category_name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('category_name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'type_name'}
                    direction={sortField === 'type_name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('type_name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'subtype_name'}
                    direction={sortField === 'subtype_name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('subtype_name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Subtype
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'section_name'}
                    direction={sortField === 'section_name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('section_name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Section
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'region_name'}
                    direction={sortField === 'region_name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('region_name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Region
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'rating_name'}
                    direction={sortField === 'rating_name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('rating_name')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Hazard & Risk Ranking
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortDirection : 'asc'}
                    onClick={() => handleSort('status')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'latest_inspection_date'}
                    direction={sortField === 'latest_inspection_date' ? sortDirection : 'asc'}
                    onClick={() => handleSort('latest_inspection_date')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Latest Inspection
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentAssets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">No assets found.</TableCell>
                </TableRow>
              ) : (
                currentAssets.map((asset) => (
                  <TableRow
                    key={`${asset.id}-${asset.code}`} // Use a potentially more unique key
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      }
                    }}
                    hover
                    onClick={() => handleAssetClick(asset.id)}
                  >
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {asset.name}
                        <Typography component="span" variant="body2" sx={{ color: theme.palette.grey[400], ml: 0.5 }}>
                          ({asset.id})
                        </Typography>
                      </Typography>
                      {asset.code && (
                        <Typography variant="caption" color="text.secondary">
                          {asset.code}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {asset.is_critical && (
                        <Chip
                          size="small"
                          label="CRITICAL"
                          icon={<WarningIcon />}
                          sx={{
                            color: '#fff',
                            backgroundColor: '#d32f2f',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            '& .MuiChip-icon': {
                              color: '#fff'
                            }
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{asset.category_name}</TableCell>
                    <TableCell>{asset.type_name}</TableCell>
                    <TableCell>{asset.subtype_name}</TableCell>
                    <TableCell>{asset.section_name || 'N/A'}</TableCell>
                    <TableCell>{asset.region_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {asset.rating_name ? (
                          <Chip
                            size="small"
                            label={asset.rating_name.replace('Cx', 'Rx')}
                            sx={{
                              color: 'white',
                              backgroundColor: asset.rating_color_code || theme.palette.grey[500],
                              fontWeight: 500,
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">N/A</Typography>
                        )}
                        {(() => {
                          const scheme = filterLookupData.ratingSchemes.find(s => s.asset_type_id === asset.asset_type_id);
                          const isEditable = !!scheme && !scheme.auto_update_from_inspection;
                          if (isEditable) {
                            return (
                              <Tooltip title="Edit Manual Rating">
                                <IconButton size="small" onClick={(e) => handleEditRating(asset, e)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={asset.status}
                        icon={statusConfig[asset.status]?.icon}
                        sx={{
                          color: statusConfig[asset.status]?.color || theme.palette.text.primary,
                          backgroundColor: statusConfig[asset.status]?.backgroundColor || alpha(theme.palette.grey[300], 0.5),
                          fontWeight: 500,
                          '& .MuiChip-icon': {
                            color: statusConfig[asset.status]?.color || theme.palette.text.primary
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>
                      {asset.latest_inspection_date ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            {new Date(asset.latest_inspection_date).toLocaleDateString('en-GB')}
                            {asset.latest_inspection_condition && asset.latest_inspection_condition !== 'N/A' ? (
                              <Chip
                                label={asset.latest_inspection_condition}
                                size="small"
                                sx={{
                                  height: '18px',
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  backgroundColor: asset.rating_color_code ? alpha(asset.rating_color_code, 0.15) : alpha(theme.palette.grey[500], 0.15),
                                  color: asset.rating_color_code || theme.palette.grey[700],
                                  border: `1px solid ${asset.rating_color_code ? alpha(asset.rating_color_code, 0.3) : alpha(theme.palette.grey[500], 0.3)}`
                                }}
                              />
                            ) : (
                              <Chip
                                label="Not Rated"
                                size="small"
                                sx={{
                                  height: '18px',
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  backgroundColor: alpha(theme.palette.grey[500], 0.15),
                                  color: theme.palette.grey[700],
                                  border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`
                                }}
                              />
                            )}
                          </Typography>
                          {asset.latest_inspection_type_name && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              fontSize: '0.6rem',
                              color: 'primary.main',
                              fontWeight: 500
                            }}>
                              {asset.latest_inspection_type_name}
                            </Typography>
                          )}
                          {asset.latest_inspection_cycle && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              fontSize: '0.6rem',
                              color: 'text.disabled',
                              fontStyle: 'italic'
                            }}>
                              Cycle {asset.latest_inspection_cycle}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View on Map">
                        <IconButton
                          size="small"
                          onClick={(e) => handleViewOnMap(asset, e)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination 
            rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
            component="div"
            count={sortedAssets.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '.MuiTablePagination-toolbar': {
                paddingRight: 3,
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontWeight: 500,
              },
              '.MuiTablePagination-select': {
                fontWeight: 500,
              },
              '.MuiTablePagination-displayedRows': {
                minWidth: '120px',
              }
            }}
            labelRowsPerPage="Show rows:"
            labelDisplayedRows={({ from, to, count }) => (
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`} assets`
            )}
          />
        </TableContainer>
      )}

      {/* Create Asset Modal */}
      <Modal
        open={openCreateModal}
        onClose={handleCloseCreateModal}
        aria-labelledby="modal-create-asset"
        aria-describedby="modal-create-new-asset"
        keepMounted
      >
        <Box sx={modalStyle}>
          <CreateAssetForm 
            onSubmit={handleFormSubmit} 
            onCancel={handleCloseCreateModal} 
            user={user} 
          />
        </Box>
      </Modal>

      {/* Asset Detail Modal */}
      <Dialog
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: theme.shadows[10],
            height: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          pb: 1,
          pt: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            <Typography variant="h6">
              Asset Details (ID: {selectedAssetId})
            </Typography>
            {user?.role === 'viewer' && (
              <Chip 
                label="Read Only" 
                size="small" 
                color="info" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={handleCloseDetailModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2, px: 3 }}>
          {detailLoading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {selectedAssetData && (
            <Box>
              {/* Asset Details Grid */}
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500, mt: 1.5 }}>
                Asset Information
              </Typography>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
              >
                <Grid container spacing={2}>
                   <Grid item xs={12} sm={6}> <Typography variant="body2"><strong>ID:</strong> {selectedAssetData.id}</Typography> </Grid>
                   <Grid item xs={12} sm={6}> <Typography variant="body2"><strong>Code:</strong> {selectedAssetData.code}</Typography> </Grid>
                   <Grid item xs={12}> <Typography variant="body2"><strong>Name:</strong> {selectedAssetData.name}</Typography> </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Category:</strong> {selectedAssetData.category_name}</Typography> </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Type:</strong> {selectedAssetData.type_name}</Typography> </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Subtype:</strong> {selectedAssetData.subtype_name}</Typography> </Grid>
                   <Grid item xs={12} sm={4}>
                     <Typography variant="body2">
                       <strong>Status:</strong>&nbsp;
                       <Chip
                         size="small"
                         label={selectedAssetData.status}
                         icon={statusConfig[selectedAssetData.status]?.icon}
                         sx={{
                           color: statusConfig[selectedAssetData.status]?.color || theme.palette.text.primary,
                           backgroundColor: statusConfig[selectedAssetData.status]?.backgroundColor || alpha(theme.palette.grey[300], 0.5),
                           fontWeight: 500,
                           '& .MuiChip-icon': {
                             color: statusConfig[selectedAssetData.status]?.color || theme.palette.text.primary
                           }
                         }}
                       />
                     </Typography>
                   </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Install Date:</strong> {selectedAssetData.install_date ? new Date(selectedAssetData.install_date).toLocaleDateString() : 'N/A'}</Typography> </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Network:</strong> {selectedAssetData.network_name || 'N/A'}</Typography> </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Section:</strong> {selectedAssetData.section_name || 'N/A'}</Typography> </Grid>
                   <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Organization:</strong> {selectedAssetData.organization_name || 'N/A'}</Typography> </Grid>

                   {/* Critical Infrastructure Toggle - Admin/Manager only */}
                   {canSetCritical && (
                     <Grid item xs={12}>
                       <Box sx={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'space-between',
                         p: 1.5,
                         mt: 1,
                         borderRadius: 1,
                         bgcolor: selectedAssetData.is_critical ? alpha('#d32f2f', 0.08) : alpha(theme.palette.grey[500], 0.08),
                         border: `1px solid ${selectedAssetData.is_critical ? alpha('#d32f2f', 0.3) : alpha(theme.palette.grey[500], 0.2)}`
                       }}>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <WarningIcon sx={{ color: selectedAssetData.is_critical ? '#d32f2f' : theme.palette.grey[500], fontSize: 20 }} />
                           <Typography variant="body2" sx={{ fontWeight: 500 }}>
                             Critical Infrastructure
                           </Typography>
                         </Box>
                         <FormControlLabel
                           control={
                             <Switch
                               checked={selectedAssetData.is_critical || false}
                               onChange={() => handleCriticalToggle(selectedAssetData.id, selectedAssetData.is_critical)}
                               disabled={criticalToggleLoading}
                               sx={{
                                 '& .MuiSwitch-switchBase.Mui-checked': {
                                   color: '#d32f2f',
                                 },
                                 '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                   backgroundColor: '#d32f2f',
                                 },
                               }}
                             />
                           }
                           label={criticalToggleLoading ? <CircularProgress size={16} /> : ''}
                           labelPlacement="start"
                           sx={{ m: 0 }}
                         />
                       </Box>
                     </Grid>
                   )}

                   {/* Show critical status for non-admin/manager users */}
                   {!canSetCritical && selectedAssetData.is_critical && (
                     <Grid item xs={12}>
                       <Box sx={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 1,
                         p: 1.5,
                         mt: 1,
                         borderRadius: 1,
                         bgcolor: alpha('#d32f2f', 0.08),
                         border: `1px solid ${alpha('#d32f2f', 0.3)}`
                       }}>
                         <WarningIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                         <Chip
                           label="CRITICAL INFRASTRUCTURE"
                           size="small"
                           sx={{
                             bgcolor: '#d32f2f',
                             color: '#fff',
                             fontWeight: 600,
                           }}
                         />
                       </Box>
                     </Grid>
                   )}
                </Grid>
              </Paper>

              {/* Components Table */}
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>Components</Typography>
              {selectedAssetData.components && selectedAssetData.components.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', boxShadow: theme.shadows[1] }}>
                  <Table size="small" aria-label="asset components table">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedAssetData.components.map((component) => (
                        <TableRow key={component.id} hover sx={{ 
                          '&:hover': { 
                            backgroundColor: alpha(theme.palette.primary.main, 0.04) 
                          }
                        }}>
                          <TableCell>{component.id}</TableCell>
                          <TableCell>{component.code || 'N/A'}</TableCell>
                          <TableCell>{component.name || 'N/A'}</TableCell>
                          <TableCell>{component.component_type_name} ({component.component_type_code})</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={component.status}
                              sx={{
                                fontSize: '0.7rem',
                                height: 22,
                                color: statusConfig[component.status]?.color || theme.palette.text.primary,
                                backgroundColor: statusConfig[component.status]?.backgroundColor || alpha(theme.palette.grey[300], 0.5)
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">No components associated with this asset.</Typography>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Button onClick={handleCloseDetailModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters Drawer */}
      <AssetFilters
        open={filterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
        assetsData={authorizedAssets}
        filterLookupData={filterLookupData}
        onRatingDataChange={handleRatingDataChange}
      />

      {/* Edit Asset Rating Modal */}
      {selectedAssetForRating && (
        <EditAssetRatingDialog
          open={isRatingDialogOpen}
          onClose={handleCloseRatingDialog}
          asset={selectedAssetForRating}
          onUpdate={() => {
            handleCloseRatingDialog();
            handleRatingUpdate();
          }}
        />
      )}

      {/* Snackbar for asset creation notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Zoom}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            '.MuiAlert-icon': {
              fontSize: '1.25rem',
              marginRight: '12px',
            },
            '.MuiAlert-message': {
              fontSize: '0.95rem',
              fontWeight: 500,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssetsPage;
